import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { House, MagnifyingGlass, Briefcase, User, ShieldCheck, Wrench, SignOut, Globe, Plus, Sparkle, HandHeart, Heart } from "@phosphor-icons/react";
import { ParentFooter } from "./ParentFooter";
import { useI18n, LANGUAGES } from "../lib/i18n";
import { useAuth } from "../lib/auth";

export function Header() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="bg-[var(--as-bg)] border-b border-[var(--as-border)] sticky top-0 z-40" data-testid="app-header">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8 py-3 md:py-4 gap-2">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 md:gap-3 shrink-0" aria-label="MYTHIVA home">
            <img
              src="/mythiva-logo.png"
              alt="MYTHIVA™"
              className="h-7 md:h-8 w-auto object-contain select-none"
              draggable="false"
            />
            <span className="hidden sm:block h-6 w-px bg-[var(--as-border)]" aria-hidden="true" />
            <span className="hidden sm:block text-[10px] uppercase tracking-[0.20em] text-[var(--as-text-secondary)] font-bold whitespace-nowrap">
              Associate Skill
            </span>
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/" end className={({isActive}) => `btn-ghost ${isActive ? "bg-[var(--as-surface-muted)]":""}`} data-testid="nav-home">
            <House size={20} weight="duotone" />{t("nav_home")}
          </NavLink>
          <NavLink to="/opportunities" className={({isActive}) => `btn-ghost ${isActive ? "bg-[var(--as-surface-muted)]":""}`} data-testid="nav-jobs">
            <Briefcase size={20} weight="duotone" />{t("nav_jobs")}
          </NavLink>
          <NavLink to="/search" className={({isActive}) => `btn-ghost ${isActive ? "bg-[var(--as-surface-muted)]":""}`} data-testid="nav-search">
            <MagnifyingGlass size={20} weight="duotone" />{t("nav_search")}
          </NavLink>
          <NavLink to="/sponsorship-programs" className={({isActive}) => `btn-ghost ${isActive ? "bg-[var(--as-surface-muted)]":""}`} data-testid="nav-programs">
            <HandHeart size={20} weight="duotone" />Sponsors
          </NavLink>
          <NavLink to="/donate" className={({isActive}) => `btn-ghost ${isActive ? "bg-[var(--as-surface-muted)]":""}`} data-testid="nav-donate">
            <Heart size={18} weight="duotone" />Donate
          </NavLink>
          <NavLink to="/investors" className={({isActive}) => `btn-ghost ${isActive ? "bg-[var(--as-surface-muted)]":""}`} data-testid="nav-investors">
            <Sparkle size={18} weight="duotone" />Why
          </NavLink>
          {user && (
            <NavLink to="/dashboard" className={({isActive}) => `btn-ghost ${isActive ? "bg-[var(--as-surface-muted)]":""}`} data-testid="nav-dashboard">
              <User size={20} weight="duotone" />{t("nav_dashboard")}
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin" className={({isActive}) => `btn-ghost ${isActive ? "bg-[var(--as-surface-muted)]":""}`} data-testid="nav-admin">
              <ShieldCheck size={20} weight="duotone" />Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
          <div className="relative">
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              data-testid="lang-select"
              className="appearance-none rounded-lg pr-6 md:pr-8 pl-7 md:pl-9 py-1.5 md:py-2 text-xs md:text-sm font-bold border border-[var(--as-border)] bg-[var(--as-surface-muted)] text-[var(--as-primary)] cursor-pointer outline-none"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            <Globe size={14} weight="duotone" className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {user ? (
            <button onClick={() => { logout(); navigate("/"); }} className="btn-ghost !px-2 !py-1.5 md:!px-3 md:!py-2" data-testid="logout-btn">
              <SignOut size={18} weight="duotone" /><span className="hidden sm:inline">{t("logout")}</span>
            </button>
          ) : (
            <>
              <Link to="/login" className="btn-ghost !px-2 !py-1.5 md:!px-3 md:!py-2 text-sm" data-testid="header-login">{t("login")}</Link>
              <Link to="/register" className="btn-primary !px-3 !py-1.5 md:!px-4 md:!py-2 text-xs md:text-sm" data-testid="header-signup">
                <Plus size={16} weight="bold" /><span className="hidden sm:inline">{t("sign_up")}</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function MobileNav() {
  const { t } = useI18n();
  const { user } = useAuth();
  const location = useLocation();
  const tabs = [
    { to: "/", icon: House, label: t("nav_home"), tid: "mob-home" },
    { to: "/opportunities", icon: Briefcase, label: t("nav_jobs"), tid: "mob-jobs" },
    { to: "/sponsorship-programs", icon: HandHeart, label: "Sponsors", tid: "mob-sponsors" },
    { to: "/donate", icon: Heart, label: "Donate", tid: "mob-donate" },
    { to: user ? "/dashboard" : "/login", icon: User, label: user ? t("nav_dashboard") : t("login"), tid: "mob-dash" },
  ];
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[var(--as-border)] safe-bottom" data-testid="mobile-nav">
      <ul className="grid grid-cols-5">
        {tabs.map((tb) => {
          const Icon = tb.icon;
          const active = location.pathname === tb.to || (tb.to !== "/" && location.pathname.startsWith(tb.to));
          return (
            <li key={tb.tid}>
              <NavLink to={tb.to} className={`flex flex-col items-center gap-0.5 py-2.5 ${active ? "text-[var(--as-accent)]" : "text-[var(--as-primary)]"}`} data-testid={tb.tid}>
                <Icon size={20} weight="duotone" />
                <span className="text-[9px] font-bold leading-tight">{tb.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <ParentFooter />
      <MobileNav />
    </div>
  );
}
